'use client'
import {
  useRedeem
} from "./chunk-7OO2W25G.mjs";
import {
  usePayment
} from "./chunk-QH2UTQBS.mjs";
import {
  __async,
  __spreadProps,
  __spreadValues
} from "./chunk-BTG5EALA.mjs";

// src/context/usePayment.ts
import { useEffect } from "react";
import { useCurrency } from "@wbk/currency";
import { useRouter } from "@wbk/config";
import { useApplePayPaymentProceed, useApplePayPaymentSession } from "@wbk/api/hooks";
import { useAuth } from "@wbk/auth";
import { Exception } from "@wbk/logger";
var usePayment2 = ({ paytabs, applePay } = {}) => {
  const payment = usePayment();
  const redeem = useRedeem();
  const { mutateAsync: applePaySession, error: applePayError } = useApplePayPaymentSession();
  const { mutateAsync: applePayProceed, error: applePayProceedError } = useApplePayPaymentProceed();
  const { user } = useAuth();
  const { exchangePriceForCurrency } = useCurrency();
  const { locale } = useRouter();
  useEffect(() => {
    if (paytabs) {
      payment.paytabs = paytabs;
    }
  }, [applePay, payment, paytabs]);
  if (!payment || !redeem) {
    throw new Error(
      "usePayment must be used within an WbkPaymentProvider, refer to usage instructions: https://github.com/nadude/webook-frontend/blob/main/packages/payment/README.md"
    );
  }
  const onApplePayPayment = (_0) => __async(void 0, [_0], function* ({ totals }) {
    if (!applePay) {
      throw new Exception({
        error: new Error("Apple Pay is not enabled, missing applePay config"),
        name: "ApplePayNotEnabledException"
      });
    }
    const event = payment.event;
    const currency = payment.currency || "SAR";
    const cardType = payment.cardType || "visa";
    const isBah = currency === "BHD";
    const networks = isBah ? ["visa", "masterCard"] : ["mada", "visa", "masterCard", "amex", "discover"];
    const isMada = cardType === "mada";
    const total = isMada ? yield exchangePriceForCurrency(totals.net_after_discount, currency, "SAR", false) : totals.net_after_discount;
    const request = {
      countryCode: isMada ? "SA" : currency.slice(0, 2),
      currencyCode: isMada ? "SAR" : currency,
      merchantCapabilities: ["supports3DS"],
      supportedNetworks: isMada ? ["mada", "amex"] : networks,
      total: {
        label: event.title,
        amount: total.toFixed(2)
      }
    };
    const session = new ApplePaySession(3, request);
    session.onvalidatemerchant = (appleEvent) => __async(void 0, null, function* () {
      try {
        const merchantSession = yield applePaySession({
          event_id: event._id,
          validationUrl: appleEvent.validationURL,
          lang: locale,
          email: user == null ? void 0 : user.email,
          is_mada: isMada || void 0
        });
        session.completeMerchantValidation(merchantSession);
      } catch (error) {
        session.completePayment({ status: ApplePaySession.STATUS_FAILURE });
        applePay.onPaymentFailed();
      }
    });
    session.onpaymentmethodselected = () => {
      session.completePaymentMethodSelection({ newTotal: request.total });
    };
    session.onpaymentauthorized = (payment2) => __async(void 0, null, function* () {
      try {
        const sessionObj = yield applePay.onPaymentAuthorized();
        if (!(sessionObj == null ? void 0 : sessionObj.payment_session_id) || !sessionObj.order_id) {
          throw new Exception({
            error: new Error("Apple Pay Checkout v2 Did Not Return Payment Session ID"),
            name: "ApplePayCheckoutNoSessionIdException"
          });
        }
        const sar = yield exchangePriceForCurrency(
          totals.net_after_discount,
          currency,
          "SAR",
          false
        );
        const orderObj = yield applePayProceed({
          lang: locale,
          order_id: sessionObj.order_id,
          payment_session_id: sessionObj.payment_session_id,
          apple_pay_token: payment2.payment.token,
          email: user == null ? void 0 : user.email,
          amount_sar: sar,
          is_mada: cardType === "mada" || void 0
        });
        if (!(orderObj == null ? void 0 : orderObj.order_id)) {
          throw new Exception({
            error: new Error("Apple Pay Checkout Proceed Did Not Return Order ID"),
            name: "ApplePayCheckoutProceedNoOrderIdException"
          });
        }
        session.completePayment({ status: ApplePaySession.STATUS_SUCCESS });
        applePay.onPaymentSuccess(orderObj);
      } catch (error) {
        session.completePayment({ status: ApplePaySession.STATUS_FAILURE });
        applePay.onPaymentFailed();
        throw error;
      }
    });
    session.begin();
  });
  return __spreadProps(__spreadValues(__spreadValues({}, payment), redeem), {
    onApplePayPayment,
    error: applePayError || applePayProceedError
  });
};

export {
  usePayment2 as usePayment
};
//# sourceMappingURL=chunk-5DRAJV7W.mjs.map